import React, { useEffect, useState, useCallback } from "react";
import { useGetDriversBreakdownMutation } from "../../../store/api/lumi/drivers";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { debounce } from "lodash";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import classes from "./MetadataDriversBreakdown.module.css";
import Scrollbars from "react-custom-scrollbars-2";
import { FilterNameSelector } from "../../DashboardComponents/DashboardFilters/shared";
import SmallMultipleDriverChart from "./SmallMultipleDriverChart";
import LabelSelector from "./LabelSelector";

const MetadataDriversBreakdown = (props) => {
  const { filters, metaDataQu, breakdownName, setBreakdownName, match_type, selectedFieldDriverName } = props;
  const metaData = metaDataQu?.metaDataWithStringType || {};
  const [getDriversBreakdown] = useGetDriversBreakdownMutation();
  const [loading, setLoading] = useState(false);
  const [processedData, setProcessedData] = useState([]);

  // LabelSelector for toggling impact types: "Positive Impact" and "Negative Impact"
  const [selectedLabels, setSelectedLabels] = useState(["Positive Impact", "Negative Impact"]);
  const toggleLabel = (label) => {
    if (selectedLabels.includes(label)) {
      setSelectedLabels(selectedLabels.filter((l) => l !== label));
    } else {
      setSelectedLabels([...selectedLabels, label]);
    }
  };

  async function getDriversData() {
    setLoading(true);
    const conceptFilter = getConceptFilter(props);
    const response = await getDriversBreakdown({
      id: props.id,
      score_field: selectedFieldDriverName,
      concept_selector: conceptFilter,
      filter: props.filters,
      match_type,
      breakdowns: [{ name: Array.isArray(breakdownName) ? breakdownName[0] : breakdownName }],
    });
    const data = response.data;
    if (!data || !Array.isArray(data) || data.length === 0 || !data[0].buckets) {
      console.error("Unexpected API response structure:", data);
      setProcessedData([]);
      setLoading(false);
      return;
    }
    const buckets = data[0].buckets;
    // Process each bucket:
    const aggregatedData = buckets
      .filter(({ drivers }) => drivers && drivers.length > 0)
      .map(({ label, drivers }) => {
        // Sort drivers by match_count descending and take the top 5 drivers.
        const sortedDrivers = [...drivers].sort((a, b) => b.match_count - a.match_count);
        const topDrivers = sortedDrivers.slice(0, 5);
        // Build chart data: each data point contains the driver name and its impact.
        const chartData = topDrivers.map(({ name, impact }) => ({
          driver: name,
          value: impact,
        }));
        return {
          location: label,
          chartData,
        };
      })
      // Sort buckets by total volume (sum of absolute impact values) descending.
      .sort((a, b) => {
        const sumA = a.chartData.reduce((sum, d) => sum + Math.abs(d.value), 0);
        const sumB = b.chartData.reduce((sum, d) => sum + Math.abs(d.value), 0);
        return sumB - sumA;
      })
      .slice(0, 100);

    // Compute global min and max impact across all buckets.
    let globalMin = 0;
    let globalMax = 0;
    aggregatedData.forEach((group) => {
      group.chartData.forEach((item) => {
        if (item.value < globalMin) globalMin = item.value;
        if (item.value > globalMax) globalMax = item.value;
      });
    });
    // Add 10% padding to the range.
    const padding = (globalMax - globalMin) * 0.1;
    globalMin = globalMin - padding;
    globalMax = globalMax + padding;

    // Attach the global Y-axis domain to each group.
    const processed = aggregatedData.map((group) => ({
      ...group,
      globalMin,
      globalMax,
    }));

    // Filter each group's data based on the selected impact type.
    const filteredData = processed.map((group) => ({
      ...group,
      chartData: group.chartData.filter((item) => {
        if (selectedLabels.includes("Positive Impact") && selectedLabels.includes("Negative Impact")) {
          return true;
        } else if (selectedLabels.includes("Positive Impact")) {
          return item.value >= 0;
        } else if (selectedLabels.includes("Negative Impact")) {
          return item.value < 0;
        }
        return true;
      }),
    }));

    setProcessedData(filteredData);
    setLoading(false);
  }

  const debouncedGetDrivers = useCallback(
    debounce(getDriversData, 500),
    [
      filters,
      breakdownName,
      props.selectedLocalConcepts,
      props.conceptList,
      match_type,
      selectedFieldDriverName,
      selectedLabels,
    ]
  );

  useEffect(() => {
    debouncedGetDrivers();
    return () => {
      debouncedGetDrivers.cancel();
    };
  }, [debouncedGetDrivers]);

  useEffect(() => {
    props.setAdditionalFilters([
      {
        key: "Special",
        label: "Select Breakdown Variable:",
        showArrow: false,
        children: (
          <FilterNameSelector
            selectedFieldName={breakdownName}
            setSelectedFieldName={setBreakdownName}
            metaData={metaData}
          />
        ),
      },
    ]);
  }, [breakdownName, metaData, props]);

  if (loading) {
    return (
      <Spin
        className="spinner-by-center"
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
      />
    );
  }

  return (
    <div
      className={classes.widgetContainer}
      style={{ height: "100%", display: "flex", flexDirection: "column" }}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      {/* LabelSelector for Positive/Negative Impact */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "8px", padding: "8px" }}>
        <LabelSelector
          labels={["Positive Impact", "Negative Impact"]}
          selectedLabels={selectedLabels}
          onToggleLabel={toggleLabel}
        />
      </div>
      {/* Scrollable area: grid of driver charts */}
      <div style={{ flex: 1 }}>
        <Scrollbars style={{ height: "100%" }}>
          {processedData.length > 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
                gap: "1.5rem",
                padding: "1rem",
              }}
            >
              {processedData.map((group, index) => (
                <SmallMultipleDriverChart
                  key={`group-${group.location}-${index}`}
                  location={group.location}
                  data={group.chartData}
                  minYAxis={group.globalMin}
                  maxYAxis={group.globalMax}
                />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "1rem" }}>No data available</div>
          )}
        </Scrollbars>
      </div>
    </div>
  );
};

export default MetadataDriversBreakdown;
